import React, { useEffect, useState } from 'react';
import { useAppStore } from '../context/AppStore';
import { Card, Button, Badge, Input, Toggle, Modal, ConfirmDialog, EmptyState } from '../components/ui';
import { providersAPI } from '../api/client';
import toast from 'react-hot-toast';

const PROVIDER_META = {
  deepseek: { icon: '🧠', description: 'Muy económico, excelente para generación de texto',          color: '#4f8ef7' },
  openai:   { icon: '🤖', description: 'GPT-4o: analiza imágenes y genera texto de alta calidad',     color: '#10a37f' },
  gemini:   { icon: '♊', description: 'Gemini 2.0 Flash: rápido, eficiente y con soporte de visión',        color: '#4285f4' },
  grok:     { icon: '𝕏', description: 'Grok-2: tono natural, ideal para X/Twitter',                  color: '#e7e7e7' },
  claude:   { icon: '⚡', description: 'Claude Sonnet: excelente para textos largos y LinkedIn',      color: '#cc785c' },
  groq:     { icon: '🔥', description: 'Llama 3.2 Vision: analiza imágenes GRATIS — ideal para análisis visual', color: '#f55036' },
};

const EMPTY_FORM = { name: '', slug: '', model: '', endpoint: '', api_key: '', temperature: 0.7, supports_vision: false, cost_per_1k: 0 };

const PRESETS = [
  { slug: 'deepseek', name: 'DeepSeek',       model: 'deepseek-chat',          endpoint: 'https://api.deepseek.com/v1',                          supports_vision: false, cost_per_1k: 0.002 },
  { slug: 'openai',   name: 'OpenAI GPT-4o',  model: 'gpt-4o',                 endpoint: 'https://api.openai.com/v1',                            supports_vision: true,  cost_per_1k: 0.015 },
  { slug: 'gemini',   name: 'Google Gemini',  model: 'gemini-2.5-flash-lite', endpoint: 'https://generativelanguage.googleapis.com/v1', supports_vision: true, cost_per_1k: 0.0014 },
  { slug: 'grok',     name: 'Grok',           model: 'grok-3-mini',            endpoint: 'https://api.x.ai/v1',                                  supports_vision: false, cost_per_1k: 0.010 },
  { slug: 'claude',   name: 'Claude',         model: 'claude-sonnet-4-20250514', endpoint: 'https://api.anthropic.com/v1',                       supports_vision: true,  cost_per_1k: 0.012 },
  { slug: 'groq',     name: 'Groq (Gratis)',  model: 'llama-3.2-11b-vision-preview', endpoint: 'https://api.groq.com/openai/v1',                supports_vision: true,  cost_per_1k: 0.0 },
];

function ProviderForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState(initial ? { ...initial, api_key: '' } : EMPTY_FORM);
  const [showKey, setShowKey] = useState(false);
  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name || (!initial && !form.api_key)) { toast.error('Nombre y API Key son requeridos'); return; }
        onSave(form);
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
    >
      {!initial && (
        <div>
          <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Usar preset</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {PRESETS.map(p => (
              <button
                type="button" key={p.slug}
                onClick={() => setForm(f => ({ ...f, ...p }))}
                style={{
                  padding: '6px 12px', borderRadius: '8px',
                  border: `1px solid ${form.slug === p.slug ? '#7c3aed' : 'var(--border)'}`,
                  background: form.slug === p.slug ? '#7c3aed20' : 'var(--bg-elevated)',
                  color: form.slug === p.slug ? '#a78bfa' : 'var(--text-muted)',
                  fontSize: '12px', fontFamily: 'var(--font-body)', cursor: 'pointer',
                }}
              >
                {PROVIDER_META[p.slug]?.icon} {p.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <Input label="Nombre"          value={form.name}     onChange={set('name')}     placeholder="Ej: DeepSeek Chat" required />
      <Input label="Modelo"          value={form.model}    onChange={set('model')}    placeholder="Ej: deepseek-chat" required />
      <Input label="Endpoint base URL" value={form.endpoint} onChange={set('endpoint')} placeholder="https://api.ejemplo.com/v1" />
      <div style={{ position: 'relative' }}>
        <Input
          label="API Key" value={form.api_key} onChange={set('api_key')}
          type={showKey ? 'text' : 'password'} placeholder="sk-..." icon="🔑"
          required={!initial} hint={initial ? 'Deja vacío para mantener la key actual' : undefined}
        />
        <button
          type="button" onClick={() => setShowKey(!showKey)}
          style={{ position: 'absolute', right: '10px', top: '32px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' }}
        >{showKey ? '🙈' : '👁'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <Input label="Temperatura (0-1)" type="number" value={form.temperature} onChange={v => set('temperature')(parseFloat(v))} hint="0 = preciso, 1 = creativo" />
        <Input label="Costo por 1K tokens ($)" type="number" value={form.cost_per_1k} onChange={v => set('cost_per_1k')(parseFloat(v))} />
      </div>
      <Toggle checked={form.supports_vision} onChange={set('supports_vision')} label="Soporta análisis de imágenes (Vision)" />
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '8px' }}>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={loading}>{initial ? 'Guardar cambios' : 'Agregar proveedor'}</Button>
      </div>
    </form>
  );
}

export default function ProvidersPage() {
  const { providers, providersLoading, fetchProviders, addProvider, updateProvider, deleteProvider, setActiveProvider } = useAppStore();
  const [showForm,  setShowForm]  = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [testing,   setTesting]   = useState(null);   // id del que está siendo testeado
  const [testResult, setTestResult] = useState({});    // { [id]: {ok, msg} }
  const [saving,    setSaving]    = useState(false);

  useEffect(() => { fetchProviders(); }, []);

  /* ── Guardar (crear / editar) ── */
  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await updateProvider(editing.id, form);
        toast.success('Proveedor actualizado ✓');
      } else {
        await addProvider(form);
        toast.success('Proveedor agregado ✓');
      }
      setShowForm(false); setEditing(null);
    } catch {
      toast.error('Error al guardar');
    }
    setSaving(false);
  };

  /* ── Test real de conexión ── */
  const handleTest = async (id) => {
    setTesting(id);
    setTestResult(r => ({ ...r, [id]: null }));
    try {
      const { data } = await providersAPI.test(id);
      if (data.success) {
        setTestResult(r => ({ ...r, [id]: { ok: true,  msg: 'Conexión exitosa ✓' } }));
        toast.success('Conexión exitosa ✓');
      } else {
        setTestResult(r => ({ ...r, [id]: { ok: false, msg: data.error || 'Error de conexión' } }));
        toast.error(`Error: ${data.error}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo conectar';
      setTestResult(r => ({ ...r, [id]: { ok: false, msg } }));
      toast.error(msg);
    }
    setTesting(null);
  };

  /* ── Activar ── */
  const handleActivate = async (id) => {
    await setActiveProvider(id);
    toast.success('Proveedor activado ✓');
  };

  /* ── Inactivar ── */
  const handleActivateImage = async (id) => {
    try {
      await providersAPI.activateImage(id);
      await fetchProviders();
      toast.success('Proveedor asignado para imágenes ✓');
    } catch {
      toast.error('Error al asignar proveedor de imágenes');
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await providersAPI.deactivate(id);
      await fetchProviders();
      toast.success('Proveedor inactivado');
    } catch {
      toast.error('Error al inactivar');
    }
  };

  const active = providers.find(p => p.is_active);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
          {active ? `Activo: ${active.name}` : 'Ningún proveedor activo'}
        </p>
        <Button icon="+" onClick={() => { setEditing(null); setShowForm(true); }}>Agregar proveedor</Button>
      </div>

      {/* Banner proveedor activo */}
      {active && (
        <div className="animate-fadeIn" style={{
          padding: '16px 20px', borderRadius: 'var(--radius-lg)',
          background: '#7c3aed15', border: '1px solid #7c3aed40',
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '28px' }}>{PROVIDER_META[active.slug]?.icon || '🤖'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '700', fontSize: '15px' }}>Proveedor activo: {active.name}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
              {active.model} · ${active.cost_per_1k}/1K tokens {active.supports_vision && '· Soporta visión 👁'}
            </div>
          </div>
          <Badge color="#7c3aed">● Activo</Badge>
        </div>
      )}

      {/* Lista */}
      {providersLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: '120px', borderRadius: 'var(--radius-lg)' }} />)}
        </div>
      ) : providers.length === 0 ? (
        <EmptyState
          icon="🤖" title="Sin proveedores"
          description="Agrega tu primer proveedor de IA para empezar a generar contenido."
          action={<Button icon="+" onClick={() => setShowForm(true)}>Agregar proveedor</Button>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {providers.map((p, i) => {
            const meta = PROVIDER_META[p.slug] || {};
            const result = testResult[p.id];
            return (
              <Card key={p.id} className={`animate-fadeIn delay-${i + 1}`} style={{ borderLeft: `3px solid ${p.is_active ? '#7c3aed' : 'transparent'}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', flexWrap: 'wrap' }}>

                  {/* Icono */}
                  <div style={{
                    width: '48px', height: '48px', borderRadius: '12px', flexShrink: 0,
                    background: `${meta.color || '#888'}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px',
                  }}>
                    {meta.icon || '🤖'}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', fontSize: '15px' }}>{p.name}</span>
                      {p.is_active
                        ? <Badge color="#10b981">● Activo</Badge>
                        : <Badge color="#50506a">○ Inactivo</Badge>
                      }
                      {p.supports_vision && <Badge color="#06b6d4">👁 Visión</Badge>}
                      {p.extra_config?.image_generation && <Badge color="#f59e0b">🎨 Imágenes</Badge>}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                      {p.model} · ${p.cost_per_1k}/1K tokens
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {meta.description}
                    </div>

                    {/* Resultado del test */}
                    {result && (
                      <div style={{
                        marginTop: '8px', padding: '6px 10px', borderRadius: '8px', fontSize: '12px',
                        background: result.ok ? '#10b98115' : '#ef444415',
                        border: `1px solid ${result.ok ? '#10b98130' : '#ef444430'}`,
                        color: result.ok ? '#10b981' : '#ef4444',
                      }}>
                        {result.ok ? '✓' : '✕'} {result.msg}
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Activar / Inactivar */}
                    {p.is_active ? (
                      <Button variant="ghost" size="sm" onClick={() => handleDeactivate(p.id)}>Inactivar</Button>
                    ) : (
                      <Button variant="success" size="sm" onClick={() => handleActivate(p.id)}>Activar</Button>
                    )}

                    {/* Usar para imágenes */}
                    <Button
                      variant={p.extra_config?.image_generation ? "success" : "secondary"}
                      size="sm"
                      onClick={() => handleActivateImage(p.id)}
                    >🎨 {p.extra_config?.image_generation ? "Usado para imágenes" : "Usar para imágenes"}</Button>

                    {/* Test real */}
                    <Button
                      variant="secondary" size="sm"
                      loading={testing === p.id}
                      onClick={() => handleTest(p.id)}
                    >
                      {testing === p.id ? 'Probando…' : 'Probar'}
                    </Button>

                    {/* Editar */}
                    <button
                      onClick={() => { setEditing(p); setShowForm(true); }}
                      style={{
                        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                        color: 'var(--text-muted)', cursor: 'pointer',
                        padding: '6px 10px', borderRadius: '8px', fontSize: '13px',
                      }}
                    >✏️</button>

                    {/* Eliminar — deshabilitado si está activo */}
                    <button
                      onClick={() => !p.is_active && setDeleting(p)}
                      disabled={p.is_active}
                      title={p.is_active ? 'Inactiva primero el proveedor para eliminarlo' : 'Eliminar'}
                      style={{
                        background: p.is_active ? 'transparent' : '#ef444415',
                        border: `1px solid ${p.is_active ? 'var(--border)' : '#ef444430'}`,
                        color: p.is_active ? 'var(--text-muted)' : '#ef4444',
                        cursor: p.is_active ? 'not-allowed' : 'pointer',
                        opacity: p.is_active ? 0.4 : 1,
                        padding: '6px 10px', borderRadius: '8px', fontSize: '13px',
                      }}
                    >🗑</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal form */}
      <Modal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        title={editing ? 'Editar proveedor' : 'Agregar proveedor de IA'}
        width={540}
      >
        <ProviderForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          loading={saving}
        />
      </Modal>

      {/* Confirm delete */}
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          await deleteProvider(deleting?.id);
          toast.success('Proveedor eliminado');
          setDeleting(null);
        }}
        title="Eliminar proveedor"
        message={`¿Eliminar "${deleting?.name}"?`}
        confirmLabel="Eliminar"
        danger
      />
    </div>
  );
}
